import { useMemo, useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import { VENDORED_DEPENDENCY_DETECTION } from '../../../../../shared/featureFlags/flagNames';
import { useParams, useQuery } from '../../../../contexts/RouterContext';
import { useIsFeatureEnabled } from '../../../../helpers/features';
import { useRejectSnippets, useSnippetById, useUnrejectSnippets } from '../../../../hooks/api/snippets';
import { getProjectBaseURLFromParams, nuc, openInNewTab } from '../../../../utils';
import { icons, IconText } from '../../../generic';
import FallbackImage from '../../../generic/FallbackImage';
import { RouterLink } from '../../../generic/Link';
import Loader from '../../../generic/Loader';
import Tabs, { useTabs } from '../../../generic/Tabs';
import ModalSnippetConversion from '../convert/ModalSnippetConversion';
import useModalSnippetConversion from '../convert/useModalSnippetConversion';
import { verifySnippetDetailsForConversion } from '../convert/utils';
import Buttons from '../Drawer/Buttons';
import Code from '../Drawer/Code/Code';
import Details from '../Drawer/Details';
import Header from '../Drawer/Header';
import RejectedBanner from '../Drawer/RejectedBanner';
import type { SnippetsQueryParams } from '../../../../../shared/snippets';
import type { RevisionRouteParams } from '../../projectDashboardTypes';

import styles from './SnippetDetails.module.less';

type DrawerTab = 'Details' | 'Compare Code';

const tabIds: Readonly<{ tabIds: DrawerTab[] }> = {
  tabIds: ['Details', 'Compare Code'],
};

/**
 * Full page snippet details view - similar to IssueDetailsV2.
 * Reuses existing drawer components but in a full page layout.
 */
const FullPageSnippetDetails: VoidFunctionComponent = () => {
  const params = useParams<RevisionRouteParams & { id: string }>();
  const { id: snippetId } = params;
  const [queryParams] = useQuery<SnippetsQueryParams>();

  const userRevisionLocator = `${params.project_id}$${params.revision_id}`;

  const { tabs, activeTab } = useTabs<DrawerTab>(tabIds);

  const { data, isLoading } = useSnippetById(userRevisionLocator, snippetId);
  const { snippet } = data || {};
  const { mutate: rejectSnippet, isPending: isRejecting } = useRejectSnippets(
    userRevisionLocator,
    { id: snippetId },
  );
  const { mutate: unrejectSnippet, isPending: isUnrejecting } = useUnrejectSnippets(
    userRevisionLocator,
    { id: snippetId },
  );

  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const selectedSnippetMatch = snippet?.matches.find(match => match.path === selectedPath);

  // snippet conversion button only shown when features enabled and
  // might be displayed disabled when snippet cannot be converted
  const shouldShowConversionButton = useIsFeatureEnabled(VENDORED_DEPENDENCY_DETECTION);
  const modalSnippetConversion = useModalSnippetConversion();
  const { shouldDisableConversion, disabledConversionMessage } = verifySnippetDetailsForConversion(snippet);

  const backToSnippetsURL = useMemo(() => {
    const projectBaseURL = getProjectBaseURLFromParams(params);
    return `${projectBaseURL}/browse/snippets`;
  }, [params]);

  if (isLoading || !snippetId) {
    return (
      <div className="container container-responsive">
        <div className={styles.body}>
          <Loader />
        </div>
      </div>
    );
  }

  if (!snippet) {
    return (
      <div className="container container-responsive">
        <div className={styles.backToLink}>
          <RouterLink to={backToSnippetsURL}>
            <IconText
              icon={icons.angleLeft}
              text="Back to Snippets"
            />
          </RouterLink>
        </div>
        <div className={styles.body}>
          <FallbackImage
            src="no-results"
            alt="Snippet not found"
            hasPadding
          >
            Snippet Not Found
          </FallbackImage>
        </div>
      </div>
    );
  }

  return (
    <div className="container container-responsive">
      <div className={styles.backToLink}>
        <RouterLink to={backToSnippetsURL}>
          <IconText
            icon={icons.angleLeft}
            text="Back to Snippets"
          />
        </RouterLink>
      </div>
      <div className={styles.body}>
        <Header snippet={snippet} />
        <Buttons
          isRejected={!!snippet.rejectionDetails}
          isRejecting={isRejecting}
          onRejectClick={() => rejectSnippet({
            ids: [snippet.id],
            path: queryParams.path,
          })}
          shouldShowConversionButton={shouldShowConversionButton}
          shouldDisableConversionButton={shouldDisableConversion}
          conversionButtonDisableMessage={disabledConversionMessage}
          onConvertClick={() => {
            modalSnippetConversion.convertSnippetDetails(snippet);
          }}
          isUnrejecting={isUnrejecting}
          onUnrejectClick={() => unrejectSnippet({
            ids: [snippet.id],
            path: queryParams.path,
          })}
          onDownloadClick={() => {
            return openInNewTab(`/api/revisions/${nuc(userRevisionLocator)}/snippets/${nuc(snippet.id)}`);
          }}
        />
        {/* When NOT comparing code, use the snippet's rejection details */}
        {activeTab !== 'Compare Code' && snippet?.rejectionDetails && (
        <RejectedBanner rejectionDetails={snippet.rejectionDetails} rejectionTarget="snippet" />
        )}
        {/* When comparing code, use the selected snippet match's rejection details */}
        {activeTab === 'Compare Code' && selectedSnippetMatch?.rejectionDetails && (
        <RejectedBanner rejectionDetails={selectedSnippetMatch.rejectionDetails} rejectionTarget="match" />
        )}
        <Tabs>
          {tabs.map(tab => (
            <Tabs.Tab
              key={tab.label}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...tab}
            />
          ))}
        </Tabs>
        {activeTab === 'Details' ? (
          <Details snippet={snippet} />
        ) : (
          <div className={styles.codeSection}>
            <Code
              userRevisionLocator={userRevisionLocator}
              selectedSnippetId={snippetId}
              matches={snippet.matches}
              selectedPath={selectedPath}
              setSelectedPath={setSelectedPath}
            />
          </div>
        )}
      </div>
      <ModalSnippetConversion
        userRevisionLocator={userRevisionLocator}
        snippetConversion={modalSnippetConversion.snippetConversion}
        setSnippetConversion={modalSnippetConversion.setSnippetConversion}
      />
    </div>
  );
};

export default FullPageSnippetDetails;
